import { useEffect, useRef, useState } from "react";
import { getFreeCoaching, FreeCoaching, PremiumFeedbackItem } from "../api/resources";
import type { ApiFetch } from "../api/base";
import { sharedKokoroSpeaker, voiceInOrder } from "./browserVoice";
import { createKokoroWorker } from "./kokoroWorker";

/** A fresh free render writes its script a few seconds after its stills; an old clip never gets one. */
export const FREE_COACHING_RETRY_MS = 10000;
export const FREE_COACHING_TRIES = 12;

/**
 * The free tier's coaching for one upload: the server's sentences at once (captions, the written list), then each
 * sentence's clip as Kokoro makes it in the browser. Null until there is a script, and always when `enabled` is
 * false (premium has its own voice).
 */
export function useFreeCoachingVoice(
  apiFetch: ApiFetch,
  sfcId: number | null,
  enabled: boolean,
  /** Bumped when the free feedback was redrawn (a re-run): the new script is fetched and voiced. */
  version = 0,
  /** Called once when there is nothing to voice: the script came back with no lines, or never came. The page then
   * stops showing the voice-over as on its way. */
  onNoScript?: () => void
): { coaching: FreeCoaching; items: PremiumFeedbackItem[]; voicing: boolean } | null {
  const [coaching, setCoaching] = useState<FreeCoaching | null>(null);
  const [items, setItems] = useState<PremiumFeedbackItem[] | null>(null);
  // Still making clips: a held cue waits for its own (LiveFreeAnalysis voicePending).
  const [voicing, setVoicing] = useState(false);
  const noScript = useRef(onNoScript);
  noScript.current = onNoScript;

  useEffect(() => {
    setCoaching(null);
    setItems(null);
    setVoicing(false);
    if (!enabled || sfcId == null) return;
    let stopped = false;
    let tries = 0;
    let timer: number | undefined;
    const ask = () => {
      tries++;
      getFreeCoaching(apiFetch, sfcId).then(
        (found) => {
          if (stopped) return;
          if (!found?.items?.length) {
            noScript.current?.();
            return;
          }
          setCoaching(found);
          setItems(found.items);
          setVoicing(true);
          void voiceInOrder(found.items, sharedKokoroSpeaker(createKokoroWorker), (voiced) => setItems(voiced), () => stopped)
            .finally(() => { if (!stopped) setVoicing(false); });
        },
        () => {
          if (stopped) return;
          if (tries >= FREE_COACHING_TRIES) {
            noScript.current?.();
            return;
          }
          timer = window.setTimeout(ask, FREE_COACHING_RETRY_MS);
        }
      );
    };
    ask();
    return () => {
      stopped = true;
      if (timer !== undefined) window.clearTimeout(timer);
    };
    // apiFetch is stable per session; asking again on its identity would refetch on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sfcId, enabled, version]);

  return coaching && items ? { coaching, items, voicing } : null;
}
